import { ListFilter } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { type SalesLeadActivity } from '@/models/salesLead'
import { ActivityGroup, activityGroup, activityGroupLabels } from './activityGroups'

interface ActivityFilterProps {
  /** The whole timeline, to count each group. */
  activities: SalesLeadActivity[]
  groups: ActivityGroup[]
  onGroupsChange: (groups: ActivityGroup[]) => void
  className?: string
}

/** Narrows the timeline to some kinds of entry; with none picked it shows everything. */
export function ActivityFilter({ activities, groups, onGroupsChange, className }: ActivityFilterProps) {
  const countByGroup = new Map<ActivityGroup, number>()
  for (const activity of activities) {
    const group = activityGroup(activity)
    countByGroup.set(group, (countByGroup.get(group) ?? 0) + 1)
  }
  const label =
    groups.length === 0
      ? 'Filter'
      : groups.length === 1
        ? activityGroupLabels[groups[0]]
        : `${groups.length} filters`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={className}>
          <ListFilter className="mr-1 size-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        <DropdownMenuLabel>Show only</DropdownMenuLabel>
        {Object.values(ActivityGroup).map((group) => {
          const count = countByGroup.get(group) ?? 0
          const isPicked = groups.includes(group)
          return (
            <DropdownMenuCheckboxItem
              key={group}
              checked={isPicked}
              disabled={count === 0 && !isPicked}
              // Stays open, so several kinds can be picked at once.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(checked) =>
                onGroupsChange(checked ? [...groups, group] : groups.filter((picked) => picked !== group))
              }
            >
              {activityGroupLabels[group]}
              <span className="text-muted-foreground ml-auto pl-4 tabular-nums">{count}</span>
            </DropdownMenuCheckboxItem>
          )
        })}
        {groups.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onGroupsChange([])}>Show all activity</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
